import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Flex,
  Link,
} from "@chakra-ui/react";

interface ResponseResult {
  success: boolean;
  action?: "accepted" | "declined";
  error?: string;
  message: string;
  prenotazioneId?: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const RespondWaitingList: React.FC = () => {
  const { listaAttesaId, action } = useParams<{ listaAttesaId: string; action: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ResponseResult | null>(null);

  useEffect(() => {
    const processResponse = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/lista-attesa/${listaAttesaId}/respond/${action}`,
          { method: "POST" }
        );
        
        const data = await response.json();
        setResult(data);
      } catch (err) {
        setResult({
          success: false,
          error: "connection_error",
          message: "Errore di connessione. Riprova più tardi.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (action === "accept" || action === "decline") {
      processResponse();
    } else {
      setLoading(false);
      setResult({
        success: false,
        error: "invalid_action",
        message: "Azione non valida.",
      });
    }
  }, [listaAttesaId, action]);

  // Modal-like container
  const ModalContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
      <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.900" backdropFilter="blur(8px)" zIndex={100} />
      <Flex position="fixed" top={0} left={0} right={0} bottom={0} zIndex={101} align="center" justify="center" p={4}>
        <Box 
          bg="gray.900" 
          w="full" 
          maxW="xl" 
          maxH="85vh"
          overflowY="auto"
          border="1px solid" 
          borderColor="whiteAlpha.200"
          display="flex"
          flexDirection="column"
        >
          {children}
        </Box>
      </Flex>
    </>
  );

  // Header component
  const Header: React.FC<{ subtitle: string; title?: string }> = ({ subtitle, title }) => (
    <Box pt={8} pb={4} px={6} flexShrink={0}>
      <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="widest" mb={title ? 2 : 0}>
        {subtitle}
      </Text>
      {title && (
        <Text fontSize="xl" fontWeight="400" color="white" textTransform="uppercase" letterSpacing="wide">
          {title}
        </Text>
      )}
    </Box>
  );

  // Footer component
  const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <HStack gap={3} p={6} borderTop="1px solid" borderColor="whiteAlpha.200" flexShrink={0} justify="center">
      {children}
    </HStack>
  );

  // Loading state
  if (loading) {
    return (
      <ModalContainer>
        <VStack py={20} spacing={4}>
          <Spinner size="xl" color="white" thickness="3px" />
          <Text color="gray.400" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
            {action === "accept" ? "Conferma in corso..." : "Elaborazione..."}
          </Text>
        </VStack>
      </ModalContainer>
    );
  }

  // Success - Accepted
  if (result?.success && result.action === "accepted") {
    return (
      <ModalContainer>
        <Header subtitle="CONFERMA" />
        <VStack spacing={6} p={6} flex={1}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="green.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="green.400">✓</Text>
          </Box>
          <Text
            fontSize="xl"
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
          >
            PRENOTAZIONE CONFERMATA
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="wide"
            textAlign="center"
            maxW="sm"
          >
            {result.message}
          </Text>
          
          <Box w="full" h="1px" bg="whiteAlpha.200" />
          
          <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200" w="full">
            <Text fontWeight="medium" textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400" mb={2}>
              PROSSIMI PASSI
            </Text>
            <VStack align="start" spacing={2}>
              <Text color="gray.300" fontSize="sm">
                Riceverai un'email di conferma con tutti i dettagli
              </Text>
              <Text color="gray.300" fontSize="sm">
                Troverai l'invito per il calendario in allegato
              </Text>
            </VStack>
          </Box>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to="/"
            bg="white" 
            color="gray.900" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "gray.200" }}
          >
            TORNA ALLA HOME
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  // Success - Declined
  if (result?.success && result.action === "declined") {
    return (
      <ModalContainer>
        <Header subtitle="CONFERMA" />
        <VStack spacing={6} p={6} flex={1}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="gray.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="gray.500">✕</Text>
          </Box>
          <Text
            fontSize="xl"
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
          >
            INVITO RIFIUTATO
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="wide"
            textAlign="center"
            maxW="sm"
          >
            Sei stato rimosso dalla lista d'attesa
          </Text>
          
          <Box w="full" h="1px" bg="whiteAlpha.200" />
          
          <Text
            fontSize="sm"
            color="gray.400"
            textAlign="center"
          >
            Speriamo di rivederti presto ai nostri eventi!
          </Text>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to="/"
            bg="transparent" 
            color="white" 
            border="1px solid" 
            borderColor="whiteAlpha.300" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "white", color: "gray.900" }}
          >
            TORNA ALLA HOME
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  // Error states
  const getErrorContent = () => {
    switch (result?.error) {
      case "not_found":
        return {
          icon: "?",
          title: "INVITO NON TROVATO",
          subtitle: "L'invito potrebbe essere già stato elaborato o non esistere.",
          color: "orange.400",
        };
      case "event_passed":
        return {
          icon: "✕",
          title: "EVENTO PASSATO",
          subtitle: "L'evento per cui hai ricevuto l'invito è già terminato.",
          color: "red.400",
        };
      case "no_seats":
        return {
          icon: "!",
          title: "POSTI NON DISPONIBILI",
          subtitle: result.message || "I posti si sono esauriti prima della conferma.",
          color: "orange.400",
          showContactInfo: true,
        };
      default:
        return {
          icon: "✕",
          title: "ERRORE",
          subtitle: result?.message || "Si è verificato un errore imprevisto.",
          color: "red.400",
        };
    }
  };

  const errorContent = getErrorContent();

  return (
    <ModalContainer>
      <Header subtitle="RISPOSTA INVITO" />
      <VStack spacing={6} p={6} flex={1} align="stretch">
        <Box
          w="80px"
          h="80px"
          borderRadius="full"
          border="2px solid"
          borderColor={errorContent.color}
          display="flex"
          alignItems="center"
          justifyContent="center"
          mx="auto"
        >
          <Text fontSize="3xl" color={errorContent.color}>{errorContent.icon}</Text>
        </Box>
        <Text
          fontSize="xl"
          fontWeight="500"
          color="white"
          textTransform="uppercase"
          letterSpacing="widest"
          textAlign="center"
        >
          {errorContent.title}
        </Text>
        <Text
          fontSize="xs"
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="wide"
          textAlign="center"
          maxW="sm"
          mx="auto"
        >
          {errorContent.subtitle}
        </Text>
        
        {errorContent.showContactInfo && (
          <>
            <Box w="full" h="1px" bg="whiteAlpha.200" />
            
            <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200">
              <Text fontWeight="medium" textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400" mb={3}>
                RIMANI IN CONTATTO
              </Text>
              <Text color="gray.300" fontSize="sm" mb={4}>
                Siamo spiacenti che i posti si siano esauriti. Per non perderti i prossimi eventi, seguici o contattaci:
              </Text>
              <VStack align="start" spacing={2}>
                <Link href="mailto:info@scarpettahomerestaurant.it" color="blue.300" fontWeight="medium" fontSize="sm">
                  info@scarpettahomerestaurant.it
                </Link>
                <Link href="tel:+393517934768" color="blue.300" fontWeight="medium" fontSize="sm">
                  +39 351 793 4768
                </Link>
              </VStack>
            </Box>
          </>
        )}
      </VStack>
      <Footer>
        <Button 
          as={RouterLink}
          to="/"
          bg="transparent" 
          color="white" 
          border="1px solid" 
          borderColor="whiteAlpha.300" 
          borderRadius="none"
          textTransform="uppercase"
          letterSpacing="widest"
          fontSize="sm"
          _hover={{ bg: "white", color: "gray.900" }}
        >
          TORNA ALLA HOME
        </Button>
      </Footer>
    </ModalContainer>
  );
};

export default RespondWaitingList;
